import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FlexyForm, FlexySelectFormStructure, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { FormInputInformations } from '@europrocurement/flexy-form/components/ControlledSelect/ControlledSelect';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { OptionType } from '@europrocurement/flexy-components';
import { type DossierArticle, type LegalAnnoucementType } from '@europrocurement/l2d-modules';

export type EditLegalAnnoucementFormProps = {
    onSubmit: (data: EditLegalAnnoucementFormFormData) => void;
    onCancel: () => void;
    dossier: DossierOpenSearch;
    prestaArticle: DossierArticle;
    legalAnnoucementTypes?: Array<LegalAnnoucementType>;
    currentType?: LegalAnnoucementType;
};

export type EditLegalAnnoucementFormFormData = {
    title: string;
    prestationId: number;
    articleId: number;
    prestaArticleId: number;
    typeAnnonce: number;
};

export type SubmittedLegalAnnoucementFormData = {
    title: string;
    prestationId: Array<number>;
    articleId: number;
    typeAnnonce: OptionType;
};

const EditLegalAnnoucementForm: React.FunctionComponent<EditLegalAnnoucementFormProps> = function (
    props,
) {
    const {
        onSubmit,
        onCancel,
        dossier,
        prestaArticle,
        legalAnnoucementTypes = [],
        currentType,
    } = props;
    const [processing, setProcessing] = useState<boolean>(false);

    const prestationChoices = dossier.formalites
        .filter((formalite) => formalite.prestationId.length > 0)
        .map((formalite) => ({
            value: formalite.prestationId[0],
            label: formalite.libelle,
        }));

    // Update Name of title input on typeAnnonce selection
    const handleSelectType = (
        e: React.SyntheticEvent,
        fieldContext: FormInputInformations,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formContext: UseFormReturn<FieldValues, any, FieldValues>,
    ) => {
        const typeAnnonceInputValue = formContext.getValues('typeAnnonce');
        if (typeAnnonceInputValue.label) {
            formContext.setValue('title', typeAnnonceInputValue.label);
        }
    };

    const handleSubmit: SubmitHandler<SubmittedLegalAnnoucementFormData> = (
        formData: SubmittedLegalAnnoucementFormData,
    ) => {
        let prestationId;

        if (prestationChoices.length === 1) {
            prestationId = prestationChoices[0].value;
        } else if (formData?.prestationId[0]) {
            prestationId = formData?.prestationId[0];
        }
        if (prestationId) {
            setProcessing(true);
            onSubmit({
                title: formData.title,
                prestationId,
                articleId: prestaArticle.article.id as number,
                prestaArticleId: prestaArticle.id,
                typeAnnonce: formData?.typeAnnonce?.value as number,
            });
        }
    };

    const formStructure: FormStructure[] = [
        {
            type: 'select',
            name: 'typeAnnonce',
            inputlabel: "Type d'annonce",
            options: legalAnnoucementTypes.map((type) => ({
                value: type.id,
                label: type.label,
            })),
            rules: {
                required: 'Ce champ est requis',
            },
            onChangeInput: handleSelectType,
        } as FlexySelectFormStructure,
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Nom de la démarche',
            rules: {
                required: 'Ce champ est requis',
            },
        },
        ...(prestationChoices.length > 1
            ? [
                  {
                      type: 'selectItems',
                      name: 'prestationId',
                      mode: 'inline',
                      inputlabel: 'Formalité associée',
                      colInline: 6,
                      items: prestationChoices,
                      multiple: false,
                      rules: {
                          required: 'Ce champ est requis',
                      },
                  } as FormStructure,
              ]
            : []),
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
        defaultValues: {
            title: prestaArticle.label,
            prestationId: prestaArticle.prestationId
                ? prestaArticle.prestationId.replace(/^\D+/g, '')
                : undefined,
            typeAnnonce: { value: currentType?.id, label: currentType?.label },
        },
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Box>
            <Typography variant="l2dh6">Modifier la démarche</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '16px',
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        padding: '16px 24px',
                        alignItems: 'center',
                        color: 'primary.dark',
                    }}
                    onClick={onCancel}
                    disabled={processing}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={formContext.handleSubmit(handleSubmit)}
                    disabled={processing}
                >
                    {processing && (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    )}
                    {!processing && (
                        <FaOptionIcon
                            icon={['fas', 'edit']}
                            sx={{ lineHeight: 0 }}
                        />
                    )}
                    Modifier l&lsquo;annonce légale
                </Button>
            </Box>
        </Box>
    );
};

export default EditLegalAnnoucementForm;
